import { useCallback, useEffect, useMemo, useState } from 'react';
import { reports, type FollowUpResults as Results, type FollowUpActionRow } from '../api/reports';
import { IconCircleAlert, IconListChecks, IconRefreshCw } from '../icons';
import { session } from '../auth/session';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { Alert } from '../ui/Field';
import { Card, PageHeader } from '../ui/Page';
import { GroupableGrid, type GridColumn, type GridExport } from '../ui/GroupableGrid';

/**
 * Aksiyon Takip Raporu — what happened after the visits.
 *
 * <p>The visit results report answers "how did the store score". This one answers the question
 * that comes next and that most audit tools never get to: "did the store fix what it was told
 * to". Every row is an action; the grid groups, sorts, filters and totals them however the
 * reader arranges it, and that arrangement is remembered per reader.</p>
 *
 * <p>It loads everything in the reader's reach and filters in the grid headers, not a bar
 * above — a follow-up report is about the state of things now, and the summary follows
 * whatever the header filters leave.</p>
 */

/** The five numbers and the aging, computed over whatever the header filters left showing. */
function summarise(rows: readonly FollowUpActionRow[]) {
  const open = rows.filter((r) => r.status !== 'Completed' && r.status !== 'Unresolved').length;
  const unresolved = rows.filter((r) => r.status === 'Unresolved').length;

  const completed = rows.filter((r) => r.status === 'Completed');
  const onTime = completed.filter((r) => r.onTime).length;
  const onTimeRate = completed.length ? Math.round((onTime / completed.length) * 100) : null;

  const closeDays = rows.map((r) => r.daysToClose).filter((n): n is number => n !== null);
  const avgClose = closeDays.length
    ? Math.round(closeDays.reduce((a, b) => a + b, 0) / closeDays.length)
    : null;

  const overdue = rows.map((r) => r.overdueDays).filter((n): n is number => n !== null);
  return {
    open,
    overdue: overdue.length,
    unresolved,
    onTimeRate,
    avgClose,
    agingFresh: overdue.filter((d) => d <= 7).length,
    agingAging: overdue.filter((d) => d > 7 && d <= 30).length,
    agingStale: overdue.filter((d) => d > 30).length,
  };
}

export function FollowUp() {
  const t = useTranslate();

  const [results, setResults] = useState<Results | null>(null);
  const [shown, setShown] = useState<readonly FollowUpActionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await reports.followUp({});
      setResults(data);
      setShown(data.actions);
      setFailed(false);
      setError(null);
    } catch (e) {
      setFailed(true);
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const s = useMemo(() => summarise(shown), [shown]);

  async function saveExcel(payload: GridExport) {
    try {
      const { blob, fileName } = await reports.exportXlsx(payload);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName ?? 'aksiyon-takip-raporu.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(describeError(e, 'error.exportFailed'));
    }
  }

  const dash = <span className="nv-subtle">—</span>;
  const day = (iso: string) => iso.split('-').reverse().join('.');
  const days = (n: number) => t('followUp.days', { n: String(n) });
  const num = (n: number | null) => (n === null ? '' : String(n));

  const columns: GridColumn<FollowUpActionRow>[] = [
    {
      id: 'location',
      header: t('followUp.location'),
      minWidth: 200,
      className: 'is-primary',
      cell: (row) => (
        <>
          <span className="nv-code">{row.locationCode}</span> {row.locationName}
        </>
      ),
      groupValue: (row) => `${row.locationCode} ${row.locationName}`,
      filterValue: (row) => `${row.locationCode} ${row.locationName}`,
      filterKind: 'select',
    },
    {
      id: 'region',
      header: t('followUp.region'),
      className: 'is-muted',
      cell: (row) => row.region ?? '—',
      groupValue: (row) => row.region ?? '—',
      filterValue: (row) => row.region ?? '',
      filterKind: 'select',
    },
    {
      id: 'operation',
      header: t('followUp.operation'),
      className: 'is-muted',
      cell: (row) => row.operation ?? '—',
      groupValue: (row) => row.operation ?? '—',
      filterValue: (row) => row.operation ?? '',
      filterKind: 'select',
    },
    {
      id: 'locationType',
      header: t('followUp.locationType'),
      className: 'is-muted',
      cell: (row) => row.locationType ?? '—',
      groupValue: (row) => row.locationType ?? '—',
      filterValue: (row) => row.locationType ?? '',
      filterKind: 'select',
    },
    {
      id: 'manager',
      header: t('followUp.regionalManager'),
      className: 'is-muted',
      cell: (row) => row.regionalManager ?? '—',
      groupValue: (row) => row.regionalManager ?? '—',
      filterValue: (row) => row.regionalManager ?? '',
      filterKind: 'select',
    },
    {
      id: 'status',
      header: t('followUp.status'),
      cell: (row) => t(`actions.status.${row.status}`),
      groupValue: (row) => t(`actions.status.${row.status}`),
      filterValue: (row) => t(`actions.status.${row.status}`),
      filterKind: 'select',
    },
    {
      id: 'assignee',
      header: t('followUp.assignee'),
      className: 'is-muted',
      cell: (row) => row.assignee || '—',
      groupValue: (row) => row.assignee || '—',
      filterValue: (row) => row.assignee,
    },
    {
      id: 'category',
      header: t('followUp.category'),
      className: 'is-muted',
      cell: (row) => row.category || '—',
      groupValue: (row) => row.category || '—',
      filterValue: (row) => row.category,
      filterKind: 'select',
    },
    {
      id: 'finding',
      header: t('followUp.question'),
      minWidth: 280,
      cell: (row) => row.finding,
      groupValue: (row) => row.finding,
      filterValue: (row) => row.finding,
      filterKind: 'select',
    },
    {
      id: 'raised',
      header: t('followUp.raised'),
      className: 'is-muted',
      cell: (row) => day(row.raisedOn),
      sortValue: (row) => row.raisedOn,
      filterValue: (row) => row.raisedOn,
      filterKind: 'date',
    },
    {
      id: 'due',
      header: t('followUp.due'),
      className: 'is-muted',
      cell: (row) => day(row.dueOn),
      sortValue: (row) => row.dueOn,
      filterValue: (row) => row.dueOn,
      filterKind: 'date',
    },
    {
      id: 'overdue',
      header: t('followUp.overdueDays'),
      align: 'right',
      cell: (row) =>
        row.overdueDays === null || row.overdueDays === 0 ? dash : <Badge tone="critical">{days(row.overdueDays)}</Badge>,
      value: (row) => row.overdueDays,
      defaultAgg: 'sum',
      formatAgg: (n) => days(n),
      filterValue: (row) => num(row.overdueDays),
    },
    {
      id: 'close',
      header: t('followUp.daysToClose'),
      align: 'right',
      cell: (row) => (row.daysToClose === null ? dash : days(row.daysToClose)),
      value: (row) => row.daysToClose,
      defaultAgg: 'avg',
      formatAgg: (n) => days(n),
      filterValue: (row) => num(row.daysToClose),
    },
    {
      id: 'open',
      header: t('followUp.daysOpen'),
      align: 'right',
      cell: (row) => (row.daysOpen === null ? dash : days(row.daysOpen)),
      value: (row) => row.daysOpen,
      defaultAgg: 'avg',
      formatAgg: (n) => days(n),
      filterValue: (row) => num(row.daysOpen),
    },
  ];

  return (
    <>
      <PageHeader crumbs={[t('app.reports')]} title={t('followUp.title')} subtitle={t('followUp.subtitle')} />

      {error ? <Alert>{error}</Alert> : null}

      {!loading && !failed ? (
        <>
          <div className="nv-stat-row">
            <Stat label={t('followUp.openTotal')} value={s.open} />
            <Stat label={t('followUp.overdueTotal')} value={s.overdue} tone={s.overdue > 0 ? 'critical' : undefined} />
            <Stat label={t('followUp.onTimeRate')} value={s.onTimeRate === null ? '—' : `%${s.onTimeRate}`} />
            <Stat
              label={t('followUp.avgCloseDays')}
              value={s.avgClose === null ? '—' : t('followUp.days', { n: String(s.avgClose) })}
            />
            <Stat label={t('followUp.unresolvedTotal')} value={s.unresolved} />
          </div>

          {/* The aging of overdue actions, said apart from the counts: one day late and two
              months late are not the same problem, and a single "overdue" number hides which. */}
          {s.overdue > 0 ? (
            <div className="nv-stat-row">
              <Stat label={t('followUp.agingFresh')} value={s.agingFresh} hint={t('followUp.agingFreshHint')} />
              <Stat label={t('followUp.agingAging')} value={s.agingAging} hint={t('followUp.agingAgingHint')} />
              <Stat
                label={t('followUp.agingStale')}
                value={s.agingStale}
                tone={s.agingStale > 0 ? 'critical' : undefined}
                hint={t('followUp.agingStaleHint')}
              />
            </div>
          ) : null}
        </>
      ) : null}

      <Card>
        <GroupableGrid
          caption={t('followUp.title')}
          storageKey={`norva.grid.follow-up.${session()?.userId ?? 'anon'}`}
          columns={columns}
          rows={results?.actions ?? []}
          getRowId={(row) => row.id}
          status={loading ? 'loading' : failed ? 'error' : 'ready'}
          onFilteredRowsChange={setShown}
          onExport={(payload) => void saveExcel(payload)}
          exportFileName="aksiyon-takip-raporu.xlsx"
          errorState={
            <EmptyState
              icon={IconCircleAlert}
              tone="danger"
              title={t('common.loadFailed')}
              text={t('common.loadFailedText')}
              action={
                <Button icon={IconRefreshCw} onClick={() => void load()}>
                  {t('common.retry')}
                </Button>
              }
            />
          }
          emptyState={<EmptyState icon={IconListChecks} title={t('followUp.empty')} text={t('followUp.emptyText')} />}
        />
      </Card>
    </>
  );
}

/** One figure in the summary row. A critical tone turns the number red when it should alarm. */
function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: 'critical';
}) {
  return (
    <div className="nv-stat" title={hint}>
      <span className="nv-stat__label">{label}</span>
      <strong className={`nv-stat__value${tone === 'critical' ? ' is-critical' : ''}`}>{value}</strong>
    </div>
  );
}
